<template>
  <div class="user-requests-list text-dark" :class="{'user-requests-list--user': isUserView}">
    <h1 v-if="!isUserView">Fraværsanmodninger</h1>
    <Tabs
      class="marg-1-bottom"
      :selected="selectedTab"
      :tabs="tabs"
      @select="selectedTab = $event">
    </Tabs>
    <Loader v-if="isLoading"></Loader>
    <table class="do-table" v-else>
      <thead class="do-table__header">
      <th class="td-created">Oprettet</th>
      <th>Hvem</th>
      <th class="td-date">Fra</th>
      <th class="td-date">Til</th>
      <th>Årsag</th>
      <th>Note</th>
      <th v-if="selectedTab !== 'requested'">Note tilbage</th>
      <th v-if="selectedTab !== 'requested'">Besvaret af</th>
      <th>{{ selectedTab === 'requested' ? '' : 'Status' }}</th>
      </thead>
      <tbody>
      <tr v-if="sortedRequests.length === 0">
        <td colspan="7" class="italic text-align-left">
          {{ emptyTableTitle }}
        </td>
      </tr>
      <tr v-for="request in sortedRequests"
          :key="request.id"
          @click="setTheEditRequest(request)">
        <td class="td-created"
            :content='request.vCreated'
            v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window'
              }">{{ request.vCreatedTimeAgo }}</td>
        <td>{{ request.vUserName }}</td>
        <td class="td-date">{{ request.vStart }}</td>
        <td class="td-date">{{ request.vEnd }}</td>
        <td>
          <span class="do-pill do-pill--white">{{ request.vSubjectTitle }}</span>
        </td>
        <td class="italic">{{ request.request_note }}</td>
        <td class="italic" v-if="selectedTab !== 'requested'">{{ request.response_note }}</td>
        <td v-if="selectedTab !== 'requested'">{{ request.vResponderName }}</td>
        <td>
          <button class="do-bttn"
                  v-if="!isUserView && selectedTab === 'requested'"
                  @click="theAnswerRequest = request">
            Besvar
          </button>
          <div class="d-bagde d-badge--yellow" v-if="isUserView && request.state === 'requested'">Afventer svar</div>
          <div class="d-bagde d-badge--green" v-else-if="request.state === 'approved'">Godkendt</div>
          <div class="d-bagde" v-if="request.state === 'rejected'">Afvist</div>
        </td>
      </tr>
      </tbody>
    </table>
    <UserRequestListAnswer
      v-if="theAnswerRequest"
      :request="theAnswerRequest"
      @submitted="onSubmittedAnswer"
      @close="theAnswerRequest = null">
    </UserRequestListAnswer>
    <UserRequestForm
      v-if="theEditRequest"
      :editRequest="theEditRequest"
      @submitted="onSubmittedAnswer"
      @close="theEditRequest = null">
    </UserRequestForm>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../docendo'
import UserRequestListAnswer from './UserRequestListAnswer.vue'
import Tabs from '../common/Tabs.vue'
import Loader from '../common/Loader.vue'
import UserRequest from './UserRequest.vue'
import UserRequestForm from './UserRequestForm.vue'

export default {
  components: {UserRequestForm, UserRequest, Loader, Tabs, UserRequestListAnswer},
  props: {
    isUserView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      requests: [],
      isLoadingSubjects: false,
      subjects: [],
      theEditRequest: null,
      theAnswerRequest: null,
      selectedTab: 'requested'
    }
  },
  computed: {
    emptyTableTitle () {
      if (this.selectedTab === 'requested') {
        return 'Ingen nye anmodninger'
      }
      if (this.selectedTab === 'approved') {
        return 'Ingen godkendte anmodninger'
      }
      if (this.selectedTab === 'rejected') {
        return 'Ingen afviste anmodninger'
      }
    },
    tabs () {
      return [
        {id: 'requested', title: 'Afventer svar'},
        {id: 'approved', title: 'Godkendte'},
        {id: 'rejected', title: 'Afviste'}
      ]
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    filteredRequests () {
      return this.requests.filter(r => r.state === this.selectedTab)
    },
    formattedRequests () {
      return this.filteredRequests.map(r => {
        return {
          ...r,
          vCreated: moment(r.created_at).format('ll [Kl.] LT'),
          vCreatedTimeAgo: moment.duration(moment().diff(moment(r.created_at))).humanize() + ' siden',
          vUserName: this.allUsersInHash[r.request_by_id] ? this.allUsersInHash[r.request_by_id]['name'] : r.request_by_id,
          vSubjectTitle: this.getSubjectTitle(r.subject_id),
          vStart: moment(r.start_time).format('ll LT'),
          vEnd: moment(r.end_time).format('ll [kl.] LT'),
          vResponderName: this.allUsersInHash[r.respond_by_id] ? this.allUsersInHash[r.respond_by_id]['name'] : r.respond_by_id
        }
      })
    },
    sortedRequests () {
      return this.formattedRequests.slice().sort((a, b) => {
        return moment(b.created_at).diff(moment(a.created_at))
      })
    }
  },
  watch: {
    schoolId: {
      immediate: true,
      handler () {
        this.readRequests()
        this.readSubjects()
      }
    }
  },
  methods: {
    setTheEditRequest (request) {
      if (!this.isUserView || request.state !== 'requested') {
        return
      }
      this.theEditRequest = request
    },
    getSubjectTitle (subjectId) {
      const subject = this.subjects.find(subject => subject.id * 1 === subjectId * 1)
      return subject ? subject.name : subjectId
    },
    readRequests () {
      if (!this.schoolId) {
        return
      }
      this.isLoading = true
      docendo.listRequests(this.schoolId, success => {
        this.requests = success
        this.isLoading = false
      }, failure => {
        console.log({failure})
      })
    },
    readSubjects () {
      this.isLoadingSubjects = true
      docendo.schoolAbsenceSubjectsList(this.schoolId, true, success => {
        this.isLoadingSubjects = false
        this.subjects = success
      }, failure => {
        console.log('something went wrong')
      })
    },
    onSubmittedAnswer () {
      this.readRequests()
    }
  }
}
</script>

<style scoped>

.user-requests-list:not(.user-requests-list--user) {
  padding: 1rem !important;
}

.do-table tbody tr:nth-child(2n) {
  background-color: #f9fafb;
}

.do-table tbody tr td {
  border-left: 1px solid #e2e8f0;
}

.do-table thead th:last-child,
.do-table tbody tr td:last-child {
  text-align: center;
  width: 100px;
}

.d-bagde {
  display: inline-block;
  border-radius: 4px;
  background-color: #fef3f2;
  border: 1px solid #fbdfde;
  color: #b91c1c;
  padding: 2px 4px;
  font-size: 11px;
  width: 80px;
  text-align: center;
}

.d-bagde.d-badge--yellow {
  border: 1px solid #facc17;
  background-color: #fefce8;
  color: #a26206;
}

.d-bagde.d-badge--green {
  border: 1px solid #ceeed7;
  background-color: #f0fef4;
  color: #008236;
}

.td-date {
  width: 140px;
  text-align: center;
}

.text-align-left {
  text-align: left !important;
}

.do-pill--white {
  background: #fff;
  font-weight: bold;
}

.do-table tbody tr:hover {
  background: aliceblue;
}

.user-requests-list.user-requests-list--user .do-table tbody tr:hover {
  cursor: pointer;
}

.td-created {
  width: 140px;
}

.pad-1-important {
  padding: 1rem !important;
}

</style>



// WEBPACK FOOTER //
// UserRequestList.vue?5cc8c1c5